import { AdEnginePlugin, type Hooks } from "../../../../core/AdEnginePlugin";
import { loadScript, pushTrack } from "utils";
import { getFandomContext } from "../../../../helpers/getFandomContext";
import type {
  LotameConfigs,
  LotameNamespace,
  LotameWindowKey,
  LotamePluginConfig,
} from "./lotame";
import { type FandomCmp, LoadTimeEvent, type TaxonomyTags } from "common-types";
import { isConsentGranted } from "../../../../helpers/isConsentGranted";

/**
 * LotamePlugin class integrates Lotame data management platform with the AdEngine.
 * Data is mapped from the {@link window.fandomContext} object and sent to Lotame.
 * The plugin is enabled by setting the `icLotame` flag in the Instant Config.
 *
 * @see Implementation basics {@link https://my.lotame.com/t/83hxvnt/lt-js-basic-implementation}
 * @see Implementation validation {@link https://my.lotame.com/t/35hxvnl/lt-js-post-implementation-validation}
 */
export class LotamePlugin extends AdEnginePlugin {
  private readonly CLIENT_ID = 17364;
  private readonly PIXEL_URL = `https://tags.crwdcntrl.net/lt/c/${this.CLIENT_ID}/lt.min.js`;
  // Tracks whether the script was loaded into the page. This prevents multiple loads.
  private isLoaded = false;

  private readonly TAXONOMY_TAGS_TO_KEEP = [
    "gnre",
    "media",
    "pform",
    "pub",
    "theme",
    "tv",
  ];

  /**
   * Maps taxonomy tags to Lotame behavior tags.
   *
   * @param {TaxonomyTags} [tags={}] - The taxonomy tags to map.
   * @returns {string[]} An array of mapped TaxonomyTags that are mapped to Lotame's behavior tags format.
   */
  private mapTaxonomyToLotameBehaviorTags(tags: TaxonomyTags = {}): string[] {
    const result = [];
    for (const [key, value] of Object.entries(tags)) {
      if (this.TAXONOMY_TAGS_TO_KEEP.includes(key)) {
        for (const v of value) {
          result.push(`${key}: ${v}`);
        }
      }
    }
    return result;
  }

  /**
   * Sets up the Lotame tag input configuration.
   *
   * This method retrieves the site tags from the Fandom context and maps them to Lotame behavior tags.
   * If the site tags are not available, it returns an empty configuration.
   *
   * @returns {LotameConfigs} The Lotame configuration object containing the mapped behavior tags and client ID.
   */
  private setupLotameTagInput(): LotameConfigs {
    const siteTags = getFandomContext()?.site?.tags;
    // In case siteTags are undefined or null, let's just return an empty object
    const behaviorTags = siteTags
      ? this.mapTaxonomyToLotameBehaviorTags(siteTags)
      : [];

    return {
      data: {
        behaviors: {
          med: behaviorTags,
        },
      },
      config: {
        clientId: this.CLIENT_ID,
        // TODO: Add in tracking implementation.
        // onTagReady: () => {},
      },
    };
  }

  /**
   * Sets up the Lotame tag input configuration.
   *
   * This method retrieves the site tags from the Fandom context and maps them to Lotame behavior tags.
   * If the site tags are not available, it returns an empty configuration.
   *
   * @returns {LotameConfigs} The Lotame configuration object containing the mapped behavior tags and client ID.
   */
  private setupLotameNamespaceConfig(): void {
    const lotameTagInput: LotameConfigs = this.setupLotameTagInput();

    const id: LotameWindowKey = `lotame_${this.CLIENT_ID}`;

    const lotameConfig = lotameTagInput.config ?? {};
    const namespace: LotameNamespace = (window[id] = {});
    namespace.config = lotameConfig;
    namespace.data = lotameTagInput.data ?? {};
    namespace.cmd = namespace.cmd ?? [];
  }

  /**
   * Sets up the Lotame namespace configuration on the window object.
   *
   * This method configures the Lotame namespace with the necessary data and configuration.
   */
  private async loadLotameScript() {
    this.setupLotameNamespaceConfig();
    try {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "lotame_started",
        time: performance.now(),
      });
      await loadScript(this.PIXEL_URL);
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "lotame_loaded",
        time: performance.now(),
      });
      this.isLoaded = true;
    } catch (error) {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "lotame_error_loading",
        time: performance.now(),
      });
      console.error("Failed to load Lotame script", error);
    }
  }

  /**
   * Checks if the Lotame plugin is enabled.
   *
   * @returns {boolean} True if the plugin is enabled, false otherwise.
   */
  static isEnabled(config: LotamePluginConfig): boolean {
    return Boolean(config?.icLotame);
  }

  /**
   * Initializes the Lotame plugin.
   *
   * This method checks if the plugin is enabled and if the script is already loaded.
   * If the plugin is enabled and the script is not loaded, it loads the Lotame script.
   */
  private init() {
    if (this.isLoaded) {
      return;
    }

    this.loadLotameScript();
  }

  /**
   * Hooks for the Lotame plugin.
   *
   * This hook listens for consent updates and initializes the plugin if consent is given.
   */
  hooks: Hooks = {
    onConsentUpdated: (fandomCmp: FandomCmp) => {
      if (!isConsentGranted(fandomCmp)) {
        return;
      }

      this.init();
    },
  };
}
